<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { onMount } from 'svelte';
    import { UsageCollectorApi, type VehicleResponse } from './UsageCollectorApi';
    import { api } from '../../../stores/api';
    import SwitchBar from '../../shared/SwitchBar/SwitchBar.svelte';
    import { SwitchBarApp } from '../../shared/SwitchBar/SwitchBarApp';

    type VehicleFilterType = 'all' | 'used-in-month' | 'active';

    export let componentProps: {
        vehicles: {id: number, email: string, webuser_id: number}[];
        customer_name: string,
        month: string,
        customer_id: number,
    };

    let allVehicles: VehicleResponse[] = [];
    let isLoading = true;
    let error: string | null = null;
    let selectedFilters: VehicleFilterType[] = ['all'];

    interface EnrichedVehicle extends VehicleResponse {
        usedInMonth: boolean;
    }

    let switchBarApp: SwitchBarApp = new SwitchBarApp();
    switchBarApp
        .setItems([
            { id: 'all', label: $_('Total') },
            { id: 'used-in-month', label: $_('used_in_month') },
            { id: 'active', label: $_('filter_active') },
        ])
        .setMultiselect(false, false)
        .selectItems(selectedFilters);

    $: enrichedVehicles = allVehicles.map(vehicle => {
        const usedInMonth = componentProps.vehicles.some(v => v.id === vehicle.id);
        return {
            ...vehicle,
            usedInMonth
        };
    }) as EnrichedVehicle[];

    $: filteredVehicles = enrichedVehicles.filter(vehicle => {
        if (selectedFilters.includes('all')) {
            return true;
        }

        // Logika AND - pojazd musi spełniać wszystkie wybrane kryteria
        let matchesAllFilters = true;

        if (selectedFilters.includes('used-in-month')) {
            if (!vehicle.usedInMonth) {
                matchesAllFilters = false;
            }
        }

        if (selectedFilters.includes('active')) {
            if (vehicle.deleted !== null) {
                matchesAllFilters = false;
            }
        }

        return matchesAllFilters;
    });

    function handleFilterChange(filters: string[]) {
        selectedFilters = filters as VehicleFilterType[];
    }

    onMount(async () => {
        try {
            allVehicles = await UsageCollectorApi.getVehiclesByCustomerId($api, componentProps.customer_id);
        } catch (e: any) {
            error = e?.message || $_('vehicle_list_fetch_error');
            console.error('Error loading vehicles:', e);
        } finally {
            isLoading = false;
        }
    });
</script>

<div class="time-story-container">
    <div class="header-compact">
        <div class="header-info">
            <h3>{componentProps.customer_name}</h3>
            <span class="month-compact">{componentProps.month}</span>
        </div>
        <div class="controls-row">
            <div class="filter-bar-compact">
                <SwitchBar {switchBarApp} onChange={handleFilterChange} />
            </div>
            {#if !isLoading && !error}
                <div class="counter-compact">
                    <span class="counter-text">
                        <strong>{filteredVehicles.length}</strong> / {enrichedVehicles.length}
                    </span>
                </div>
            {/if}
        </div>
    </div>

    {#if isLoading}
        <p class="status-message">{$_('loading_vehicle_list')}</p>
    {:else if error}
        <p class="status-message error">{error}</p>
    {:else}
        <div class="table-wrapper">
            <table class="table-header-fixed">
                <thead>
                    <tr>
                        <th>Vehicle ID</th>
                        <th>Email</th>
                        <th>Webuser ID</th>
                        <th>{$_('used_in_month')}</th>
                        <th>Status</th>
                    </tr>
                </thead>
            </table>
            <div class="table-body-scrollable">
                <table>
                    <tbody>
                        {#each filteredVehicles as vehicle}
                            <tr class:used={vehicle.usedInMonth} class:deleted={vehicle.deleted !== null}>
                                <td>{vehicle.id}</td>
                                <td>{vehicle.email || '-'}</td>
                                <td>{vehicle.webuser_id || '-'}</td>
                                <td>
                                    {#if vehicle.usedInMonth}
                                        <span class="badge badge-used">✓ {$_('badge_yes')}</span>
                                    {:else}
                                        <span class="badge badge-not-used">{$_('No')}</span>
                                    {/if}
                                </td>
                                <td>
                                    {#if vehicle.deleted !== null}
                                        <span class="badge badge-deleted">{$_('badge_deleted')}</span>
                                    {:else}
                                        <span class="badge badge-active">{$_('Vehicle_state_active')}</span>
                                    {/if}
                                </td>
                            </tr>
                        {:else}
                            <tr>
                                <td colspan="5" class="no-data">{$_('no_vehicles_match_criteria')}</td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        </div>
    {/if}
</div>

<style>
    .time-story-container {
        width: 100%;
        padding: 15px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-height: 85vh;
        overflow: hidden;
    }

    .header-compact {
        flex-shrink: 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 15px;
        padding-bottom: 8px;
        border-bottom: 1px solid #e0e0e0;
    }

    .header-info {
        display: flex;
        align-items: baseline;
        gap: 10px;
    }

    .header-info h3 {
        margin: 0;
        font-size: 1.2em;
        color: #333;
        font-weight: 600;
    }

    .month-compact {
        font-size: 0.85em;
        color: #888;
        font-weight: 400;
    }

    .controls-row {
        display: flex;
        align-items: center;
        gap: 15px;
    }

    .filter-bar-compact {
        flex-shrink: 0;
    }

    .counter-compact {
        flex-shrink: 0;
        padding: 4px 10px;
        background-color: #f8f8f8;
        border-radius: 3px;
        border: 1px solid #e0e0e0;
        white-space: nowrap;
    }

    .counter-text {
        font-size: 0.85em;
        color: #666;
    }

    .counter-text strong {
        color: #333;
        font-weight: 600;
    }

    .status-message {
        padding: 20px;
        text-align: center;
        font-size: 1.1em;
        color: #666;
    }

    .status-message.error {
        color: #d32f2f;
        background-color: #ffebee;
        border-radius: 4px;
    }

    .table-wrapper {
        display: flex;
        flex-direction: column;
        border: 1px solid #ddd;
        border-radius: 4px;
        overflow: hidden;
        background-color: white;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .table-header-fixed {
        width: 100%;
        border-collapse: collapse;
        background-color: #f5f5f5;
        flex-shrink: 0;
    }

    .table-header-fixed thead {
        background-color: #f5f5f5;
    }

    .table-body-scrollable {
        overflow-y: auto;
        max-height: 600px;
    }

    table {
        width: 100%;
        border-collapse: collapse;
        background-color: white;
    }

    th {
        padding: 12px;
        text-align: left;
        font-weight: 600;
        color: #333;
        border-bottom: 2px solid #ddd;
        background-color: #f5f5f5;
    }

    td {
        padding: 10px 12px;
        border-bottom: 1px solid #eee;
    }

    tbody tr:hover {
        background-color: #f9f9f9;
    }

    tbody tr.used {
        background-color: #e8f5e9;
    }

    tbody tr.used:hover {
        background-color: #c8e6c9;
    }

    tbody tr.deleted {
        opacity: 0.6;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    .no-data {
        text-align: center !important;
        color: #999;
        font-style: italic;
        padding: 20px !important;
    }

    .badge {
        display: inline-block;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 0.85em;
        font-weight: 500;
    }

    .badge-used {
        background-color: #4caf50;
        color: white;
    }

    .badge-not-used {
        background-color: #e0e0e0;
        color: #666;
    }

    .badge-active {
        background-color: #2196f3;
        color: white;
    }

    .badge-deleted {
        background-color: #f44336;
        color: white;
    }

    /* Wyrównanie szerokości kolumn między thead i tbody */
    .table-header-fixed th:nth-child(1),
    .table-body-scrollable td:nth-child(1) {
        width: 12%;
    }

    .table-header-fixed th:nth-child(2),
    .table-body-scrollable td:nth-child(2) {
        width: 30%;
    }

    .table-header-fixed th:nth-child(3),
    .table-body-scrollable td:nth-child(3) {
        width: 15%;
    }

    .table-header-fixed th:nth-child(4),
    .table-body-scrollable td:nth-child(4),
    .table-header-fixed th:nth-child(5),
    .table-body-scrollable td:nth-child(5) {
        width: 21.5%;
    }

    /* Scrollbar styling */
    .table-body-scrollable::-webkit-scrollbar {
        width: 8px;
    }

    .table-body-scrollable::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    .table-body-scrollable::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }

    .table-body-scrollable::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
</style>
